import mediaQuery from 'css-mediaquery';
import { TFunction } from 'i18next';
import { API_V1_HOSTNAME_KEY, API_V4_HOSTNAME_KEY } from '../localStorageKeys';
import { AddressComponents, Feature, LocationV1, LocationV4, SelectOption } from '../types';
import { UserRole } from '../context/UserContext';
import salesforceCounties from './salesforceCounties';
import { IMAGE_EXTENSIONS } from '../constants';

export const slugify = (string: string) =>
  string
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export const initials = (name: string) =>
  name
    ?.match(/(^\S?|\s\S)?/g)
    ?.map((v) => v.trim())
    .join('')
    .match(/(^\S|\S$)?/g)
    ?.join('')
    .toLocaleUpperCase();

export const createMatchMedia = (width) => (query) => ({
  matches: mediaQuery.match(query, {
    width,
  }),
  addEventListener: jest.fn(),
  removeEventListener: jest.fn(),

  media: query,
  onchange: null,
  addListener: jest.fn(),
  removeListener: jest.fn(),
  dispatchEvent: jest.fn(),
});

export const formatDates = (date: Date, date2: Date) => {
  const formattedDate1 = new Date(date.toDateString());
  const formattedDate2 = new Date(date2.toDateString());
  formattedDate1.setUTCHours(0, 0, 0, 0);
  formattedDate2.setUTCHours(0, 0, 0, 0);
  return [formattedDate1.getTime(), formattedDate2.getTime()];
};

export const getAge = (date: Date) => {
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const month = today.getMonth() - date.getMonth();
  if (month < 0 || (month === 0 && today.getDate() < date.getDate())) {
    age -= 1;
  }

  return age;
};

export const dateAfterToday = (dates: number[]) => dates[0] >= dates[1];
export const dateTodayOrAfter = (dates: number[]) => dates[0] >= dates[1];
export const datesAreTheSame = (dates: number[]) => dates[0] === dates[1];

export const sessionHasBeenCleared = (url: string): boolean => {
  const apiLocalStorageKeys = [API_V1_HOSTNAME_KEY, API_V4_HOSTNAME_KEY];

  const hostnameErasedFromLocalStorage = apiLocalStorageKeys.reduce(
    (result: boolean, key: string) => result || localStorage.getItem(key) === null,
    false
  );

  return url.startsWith('null') && hostnameErasedFromLocalStorage;
};

export const checkChildLinkActive = (locationPath: string, paths: string[]) =>
  paths.find((path) => locationPath.includes(path)) != null;

export const isInFeature = (labsFeatures: Feature[], feature: string) =>
  labsFeatures?.filter((labsFeature) => labsFeature.FeatureName === feature).length > 0;

export const hasModule = (modules: UserRole[] | undefined, module: string): boolean => {
  if (!modules) {
    return false;
  }

  return modules.filter((el) => el.RoleName === module).length > 0;
};

export const getBaseDomain = (domain: string) => {
  const split = new URL(domain).hostname.split('.');
  return split.length <= 2 ? split.join('.') : split.slice(1).join('.');
};

// only converts base level of object. If it contains an object or array inside, its not converted
export const convertObjectToStringValues = (objectData: unknown): Record<string, string> => {
  const newData = deepClone(objectData);
  Object.keys(newData).forEach((k) => {
    if (typeof newData[k] === 'number') {
      newData[k] = newData[k].toString();
    } else if (newData[k] == null || typeof newData[k] === 'boolean') {
      newData[k] = (newData[k] || '').toString();
    }
  });
  return newData;
};

export const getAddressComponentOfTypeLongName = (
  addressComponents: AddressComponents[],
  typeToFind: string
) => {
  const result = addressComponents.find((addressComponent: any) =>
    addressComponent.Types.find((type: any) => type === typeToFind)
  );
  return result?.LongName;
};

export const getAddressComponentOfTypeShortName = (
  addressComponents: AddressComponents[],
  typeToFind: string
) => {
  const result = addressComponents.find((addressComponent: any) =>
    addressComponent.Types.find((type: any) => type === typeToFind)
  );
  return result?.ShortName;
};

export const setFieldOnSessionStorage = (
  formName: string,
  objectId: string,
  name: string,
  value: string | boolean | number
) => {
  const savedObjects = JSON.parse(sessionStorage.getItem(formName) || '{}');
  const currentObject = savedObjects[objectId];

  sessionStorage.setItem(
    formName,
    JSON.stringify({
      ...savedObjects,
      [objectId]: { ...currentObject, [name]: value },
    })
  );
};

export const skipSentry = (error) => {
  if (error?.code === 401) return true;

  if (
    error?.code === undefined &&
    (error.message === 'Failed to fetch' ||
      error.message === 'Load failed' ||
      error.message === 'NetworkError when attempting to fetch resource.')
  )
    return true;

  return false;
};

export const InvalidMoneyFieldOnPaste = (value: string) => /[eE+-]/.test(value);

export const InvalidMoneyFieldCharacters = ['e', 'E', '+', '-'];

export const testDecimalFieldFormat = (value: string, beforeDp: number, afterDp: number) =>
  new RegExp(`^-?\\d{0,${beforeDp}}(?:\\.\\d{0,${afterDp}})?$`).test(value);

export function groupBy(
  dataset: unknown[],
  keyGetter: (it: unknown) => string | number
): unknown[][] {
  const result = [];

  dataset.forEach((item) => {
    const key = keyGetter(item);
    const collectionForKey = result[key];
    if (!collectionForKey) {
      result[key] = [item];
      return;
    }
    collectionForKey.push(item);
  });

  return result;
}

export const toFixed = (amount: number, digits: number): number => {
  const pow = 10 ** digits;

  return Math.round(amount * pow) / pow;
};

export const formatDateWithLeadingZeroes = (date: Date) =>
  `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
    .getDate()
    .toString()
    .padStart(2, '0')}`;

export const locationV1toV4 = (locationV1: LocationV1): LocationV4 => {
  const locationV4: LocationV4 = {
    Id: locationV1?.id,
    Name: locationV1?.name,
    Description: locationV1?.description,
    Address1: locationV1?.address1,
    Address2: locationV1?.address2,
    Town: locationV1?.town,
    County: locationV1?.county,
    PostCode: locationV1?.postCode,
    LocationAreaId: locationV1?.locationAreaId,
    EmailAddress: locationV1?.emailAddress,
    NoOfTopSellers: locationV1?.noOfTopSellers,
    ShowBarTabs: locationV1?.showBarTabs,
    BarTabSpendLimit: locationV1?.barTabSpendLimit,
    NumberOfBarTabs: locationV1?.numberOfBarTabs,
    IsWarehouse: locationV1?.isWarehouse,
    PhoneNumber: locationV1?.phoneNumber,
    PredefinedLocaleSettingsId: locationV1?.predefinedLocaleSettingsId,
    DateFormat: locationV1?.dateFormat,
    ShortTimeFormat: locationV1?.shortTimeFormat,
    LanguageId: locationV1?.languageId || 1, // TODO: Check default value OR if value comes from Salesforce
    DecimalSeparator: locationV1?.decimalSeparator,
    GroupSeparator: locationV1?.groupSeparator,
    LongTimeFormat: locationV1?.longTimeFormat,
    TimeZoneId: locationV1?.timeZoneId,
    CountryName: locationV1?.countryName,
    CurrencyId: parseInt(locationV1?.currencyId, 10) || 1, // TODO: Check default value OR if value comes from Salesforce
  };

  return locationV4;
};

// ISO 4217 valid currency codes.
const validCurrencyCodes = [
  'MMK',
  'BIF',
  'KHR',
  'CAD',
  'CVE',
  'KYD',
  'LKR',
  'CLP',
  'CNY',
  'COP',
  'KMF',
  'CRC',
  'HRK',
  'CUP',
  'CYP',
  'CZK',
  'DKK',
  'DOP',
  'SVC',
  'ETB',
  'ERN',
  'EEK',
  'FKP',
  'FJD',
  'DJF',
  'GMD',
  'GIP',
  'GTQ',
  'GNF',
  'GYD',
  'HTG',
  'HNL',
  'HKD',
  'HUF',
  'ISK',
  'INR',
  'IDR',
  'IRR',
  'IQD',
  'ILS',
  'JMD',
  'JPY',
  'KZT',
  'JOD',
  'KES',
  'KPW',
  'KRW',
  'KWD',
  'KGS',
  'LAK',
  'LBP',
  'LSL',
  'LVL',
  'LRD',
  'LYD',
  'LTL',
  'MOP',
  'MWK',
  'MYR',
  'MVR',
  'MTL',
  'MRO',
  'MUR',
  'MXN',
  'MNT',
  'MDL',
  'MAD',
  'OMR',
  'NAD',
  'NPR',
  'ANG',
  'AWG',
  'VUV',
  'NZD',
  'NIO',
  'NGN',
  'NOK',
  'PKR',
  'PAB',
  'PGK',
  'PYG',
  'PEN',
  'PHP',
  'GWP',
  'QAR',
  'RUB',
  'RWF',
  'SHP',
  'STD',
  'SAR',
  'SCR',
  'SLL',
  'SGD',
  'SKK',
  'VND',
  'SOS',
  'ZAR',
  'ZWD',
  'SZL',
  'SEK',
  'CHF',
  'SYP',
  'THB',
  'TOP',
  'TTD',
  'AED',
  'TND',
  'TMM',
  'UGX',
  'MKD',
  'RUR',
  'EGP',
  'GBP',
  'TZS',
  'USD',
  'UYU',
  'UZS',
  'WST',
  'YER',
  'ZMK',
  'TWD',
  'GHS',
  'VEF',
  'SDG',
  'UYI',
  'RSD',
  'MZN',
  'AZN',
  'RON',
  'CHE',
  'CHW',
  'TRY',
  'XAF',
  'XCD',
  'XOF',
  'XPF',
  'XBA',
  'XBB',
  'XBC',
  'XBD',
  'XAU',
  'XDR',
  'XAG',
  'XPT',
  'XTS',
  'XPD',
  'SRD',
  'MGA',
  'COU',
  'AFN',
  'TJS',
  'AOA',
  'BYR',
  'BGN',
  'CDF',
  'BAM',
  'EUR',
  'MXV',
  'UAH',
  'GEL',
  'BOV',
  'PLN',
  'BRL',
  'CLF',
  'USN',
  'USS',
  'XXX',
  'ALL',
  'DZD',
  'ARS',
  'AMD',
  'AUD',
  'BSD',
  'BHD',
  'BDT',
  'BBD',
  'BZD',
  'BMD',
  'BTN',
  'BOB',
  'BWP',
  'BND',
  'SBD',
];

// Currency display helper function. When a currency is blank or invalid will display the symbol next to it.
export const currencyDisplay = (value?: number, locale?: string, currency?: string) =>
  value !== undefined
    ? Intl.NumberFormat(locale, {
        ...(validCurrencyCodes.includes(currency.toUpperCase())
          ? { currency, style: 'currency', currencyDisplay: 'narrowSymbol' }
          : {}),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) +
      (!validCurrencyCodes.includes(currency.toUpperCase()) ? ` ${currency}` : '')
    : '';

export const validatePostCode = (val: string, country: any) => {
  if (val && country === 'GB') {
    return Boolean(val.match(/^[a-zA-Z]{1,2}\d{1}[0-9a-zA-Z]?( ?\d{1}[a-zA-Z]{2})?$/));
  }

  if (val && country === 'US') {
    return Boolean(val.match(/(^\d{5}$)|(^\d{5}-\d{4}$)/));
  }

  if (val && country === 'AU') {
    return Boolean(val.match(/^\d{4}$/));
  }

  if (val && country === 'ES') {
    return Boolean(val.match(/(?:0[1-9]\d{3}|[1-4]\d{4}|5[0-2]\d{3})/));
  }

  if (val && country === 'IE') {
    return Boolean(val.match(/^[A-Za-z]\d[\dWw] [\dA-Za-z]{4}$/));
  }

  if (val && country === 'CA') {
    return Boolean(val.match(/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/));
  }

  if (val && country === 'NZ') {
    return Boolean(val.match(/^\d{4}$/));
  }

  return !!val; // if we have a post code but no validation for the user country, just pass
};

export const pad = (n: number) => n.toString(10).padStart(2, '0');

export const formatDate = (date: Date): string =>
  date ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` : '';

export const salesforceCountyOptionsByCountry = (country: string) =>
  salesforceCounties[country] || [];

export const checkAndFormatCountry = (
  country: string,
  countries: { value: string; label: string }[]
) => {
  // Check if country is ISO code or full name
  // If > 2 than it's full name, search and return ISO
  if (country?.length > 2) {
    const countryISOCode = countries.find(
      (countryObject) => countryObject.label === country
    )?.value;

    return countryISOCode || null;
  }

  // If already ISO code, then return
  return country;
};

export const formatAndTranslateOptionsList = (optionsList: SelectOption[], t: TFunction) =>
  optionsList.map((item) => ({
    ...item,
    label: t(item.label),
    description: t(item.description),
  }));

export const isFileImage = (file: File) => {
  for (let i = 0; i < IMAGE_EXTENSIONS.length; i += 1) {
    if (file.name.includes(IMAGE_EXTENSIONS[i])) return true;
  }

  return false;
};